import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────

export interface Notification {
  id: string;
  eventType: string;
  payload: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

export interface UnreadCount {
  count: number;
}

// ── Queries ───────────────────────────────────────────────────

/** Most recent 50 notifications for the current user. */
export function useNotifications() {
  return useQuery<Notification[]>({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<Notification[]>('/api/notifications'),
  });
}

/** Unread notification count — polled every 60 seconds. */
export function useUnreadCount() {
  return useQuery<UnreadCount>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<UnreadCount>('/api/notifications/unread-count'),
    refetchInterval: 60_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put(`/api/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.put('/api/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
